import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { Container, Stack, Typography, Box, Paper } from '@mui/material';
import { IProject, IWorkProfile } from '../../types/global/helper';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { CastingDetails } from './CastingDetails';
import { PhotoGrid } from './PhotoGrid';
import { DemoReelGrid } from './DemoReelGrid';
import { AudioReelGrid } from './AudioReelGrid';
import { TMediaType, UserProjectSearchDialogue } from '../UserProjectSearchDialogue/UserProjectSearchDialogue';
import { generateShareLink, getIdForIbjects } from '../../utils/AppUtils';
import { PhotoUploadedWithCropAndCompress } from '../EsForm/ProjectThumbnailUpload';
import { theme } from '../../constants/theme';
import { IApplication } from '../../types/global/ListRender.types';
import { VideoThumbnail } from '../VideoPlayer/VideoThumbnail';
import { ThemeStyle } from '../../module/ThemeManager';
import { batch, useDispatch } from 'react-redux';
import { setmedia } from '../../redux/project/actions';
import { setMute } from '../../redux/player/action';
import { setVideoViewCount } from '../../redux/adverts/action';
import CustomButton from '../Button/CustomButton';
import { useAuth } from '../../module/AuthContext';
import { LinkBox } from '../LinkBox';
import Compressor from 'compressorjs';
import { blobToDataURL } from '../ProfileHeaderUpload/helpers';
import { v4 as uuidv4 } from 'uuid';

export type TUpdateBody = {
    body: any;
    uploads: any;
};
export interface IWorkProfileProps {
    workProfile?: IWorkProfile;
    isEditMode?: boolean;
    onChange?: (payload: TUpdateBody) => void;
    isPreviewMode?: boolean;
    applicationData?: IApplication;
    onAcceptApplication?: () => void;
    onRejectApplication?: () => void;
    onMessageApplication?: () => void;
}

// Component to display application media and notes
const ApplicationMediaAndNotes: React.FC<{
    attached_media?: IProject[];
    note?: string;
    themeStyle: ThemeStyle;
    onClickPlay?: (video: IProject) => void;
}> = memo(({ attached_media, note, themeStyle, onClickPlay }) => {
    // Use useRef to track already played videos to avoid multiple view count updates
    const playedVideosRef = useRef<Set<string>>(new Set());

    const handleVideoClick = useCallback(
        (video: IProject) => {
            if (onClickPlay) {
                // Check if this video was already played
                const videoId = video._id || '';
                if (!playedVideosRef.current.has(videoId)) {
                    playedVideosRef.current.add(videoId);
                    onClickPlay(video);
                } else {
                    // For already played videos, just call onClickPlay without view count update
                    // We'll use the videoId to determine whether to update view count
                    onClickPlay(video);
                }
            }
        },
        [onClickPlay],
    );

    return (
        <Stack spacing={2} mb={3}>
            {attached_media && (
                <Box>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '18px',
                            mb: 1,
                        }}>
                        Applicant Video
                    </Typography>
                    <Stack>
                        {attached_media?.map((video, index) => (
                            <VideoThumbnail
                                key={`video-${video._id || index}`}
                                project={video}
                                onClick={() => handleVideoClick(video)}
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            {note && (
                <Box>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '18px',
                            mb: 1,
                        }}>
                        Applicant Note
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            backgroundColor: theme[themeStyle as keyof typeof theme].form_black,
                            borderRadius: '8px',
                        }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '14px',
                                whiteSpace: 'pre-wrap',
                            }}>
                            {note}
                        </Typography>
                    </Paper>
                </Box>
            )}
        </Stack>
    );
});

// const EXEMPTED = ['profile_type', 'photos', 'profile_picture', 'demo_reel', 'audio_reel'];
const CastingProfile: React.FC<IWorkProfileProps> = memo(
    ({
        workProfile,
        isEditMode,
        onChange,
        isPreviewMode,
        applicationData,
        onAcceptApplication,
        onRejectApplication,
        onMessageApplication,
    }) => {
        const { themeStyle, device } = useThemeContext();
        const [form, setForm] = useState<IWorkProfile>(workProfile!);
        const [uploads, setUploads] = useState<any>({});
        const [uploadField, setUploadField] = useState('');
        const fileInputRef = useRef<HTMLInputElement>(null);
        const [openSearch, setOpenSearch] = useState<boolean>(false);
        const [mediaType, setMediaType] = useState<TMediaType>();
        const [, setOpenDetailsForm] = useState(false);
        const viewCountUpdatedVideos = useRef<Set<string>>(new Set());
        const { isSignedIn, user } = useAuth();
        const dispatch = useDispatch();

        // --- MEDIA FORM STATE ---
        const [mediaForm, setMediaForm] = useState({
            profile_picture: workProfile?.profile_picture || '',
            photos: workProfile?.photos || [],
            demo_reel: workProfile?.demo_reel || [],
            audio_reel: workProfile?.audio_reel || [],
        });
        const [savingMedia, setSavingMedia] = useState(false);

        // Sync mediaForm with workProfile changes
        useEffect(() => {
            if (workProfile) {
                setMediaForm({
                    profile_picture: workProfile.profile_picture || '',
                    photos: workProfile.photos || [],
                    demo_reel: workProfile.demo_reel || [],
                    audio_reel: workProfile.audio_reel || [],
                });
                setForm(workProfile);
                setUploads({});
            }
        }, [workProfile, isEditMode]);

        // --- MEDIA CHANGE HANDLERS ---
        const handleMediaChange = (field: keyof typeof mediaForm, value: any) => {
            setMediaForm(prev => {
                const currentValue = prev[field];
                let updatedValue;

                if (Array.isArray(currentValue)) {
                    if (Array.isArray(value)) {
                        updatedValue = [...currentValue, ...value];
                    } else {
                        updatedValue = [...currentValue, value];
                    }
                } else {
                    updatedValue = value;
                }

                return {
                    ...prev,
                    [field]: updatedValue,
                };
            });
        };

        // --- SAVE MEDIA HANDLER ---
        const handleSaveMedia = () => {
            setSavingMedia(true);
            if (onChange) {
                onChange({
                    body: {
                        demo_reel: mediaForm.demo_reel.map(media => media._id),
                        audio_reel: mediaForm.audio_reel.map(media => media._id),
                        photos: mediaForm?.photos ?? [], // To ensure that we have the old photos
                    },
                    uploads: {
                        profile_picture: uploads.profile_picture,
                        photos: uploads.photos,
                    }, // handle uploads if needed
                });
            }
            setTimeout(() => setSavingMedia(false), 1500); // TEMP: Remove when listening to redux
        };

        // Use useCallback to memoize the function to prevent re-creation on each render
        const handleOnClickPlayForActsAndSorts = useCallback(
            (project: IProject) => {
                const videoId = project._id || '';

                // Check if we've already updated the view count for this video
                const hasUpdatedViewCount = viewCountUpdatedVideos.current.has(videoId);

                // Always set the media for playback
                dispatch(
                    setmedia({
                        media: project,
                        mediaType: 'act',
                    }),
                );

                // Only update view count once per video
                if (!hasUpdatedViewCount) {
                    viewCountUpdatedVideos.current.add(videoId);

                    // Use a debounced version of view count update
                    setTimeout(() => {
                        dispatch(setVideoViewCount(1));
                    }, 300);
                }

                batch(() => {
                    dispatch(setMute(false));
                });
            },
            [dispatch],
        );

        // Image compression utility function
        const compressImage = (file: File): Promise<{ compressedDataUrl: string; compressedFile: File }> => {
            return new Promise((resolve, reject) => {
                console.log(`Original file size: ${(file.size / 1024).toFixed(2)} KB`);

                new Compressor(file, {
                    quality: 0.7,
                    maxWidth: 1600,
                    maxHeight: 1600,
                    convertSize: 100 * 1024, // 100KB target size
                    success: compressedFile => {
                        console.log(`Compressed file size: ${(compressedFile.size / 1024).toFixed(2)} KB`);
                        console.log(
                            `Compression ratio: ${((1 - compressedFile.size / file.size) * 100).toFixed(1)}% reduction`,
                        );

                        blobToDataURL(compressedFile)
                            .then(compressedDataUrl => {
                                const finalFile = new File([compressedFile], uuidv4(), {
                                    type: compressedFile.type,
                                });
                                resolve({
                                    compressedDataUrl,
                                    compressedFile: finalFile,
                                });
                            })
                            .catch(reject);
                    },
                    error: reject,
                });
            });
        };

        const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            if (files && files.length > 0) {
                const newFiles: File[] = Array.from(files);

                try {
                    if (uploadField === 'profile_picture') {
                        // Compress single profile picture
                        const { compressedDataUrl, compressedFile } = await compressImage(newFiles[0]);
                        handleMediaChange('profile_picture', compressedDataUrl);
                        setUploads((prevState: any) => ({
                            ...prevState,
                            profile_picture: compressedFile,
                        }));
                    } else if (uploadField === 'photos') {
                        // Compress multiple photos
                        const compressionPromises = newFiles.map(file => compressImage(file));
                        const compressedResults = await Promise.all(compressionPromises);

                        const compressedDataUrls = compressedResults.map(result => result.compressedDataUrl);
                        const compressedFiles = compressedResults.map(result => result.compressedFile);

                        handleMediaChange('photos', compressedDataUrls);
                        setUploads((prevState: any) => ({
                            ...prevState,
                            photos: [...(prevState.photos || []), ...compressedFiles],
                        }));
                    }
                } catch (error) {
                    console.error('Error compressing files: ', error);
                }
            }
        };

        const handleUpload = useCallback((field: string) => {
            if (fileInputRef.current) {
                setUploadField(field);
                fileInputRef.current.click();
            }
        }, []);

        const onSubmit = useCallback((payload: any[], field: string) => {
            setMediaForm((prevState: any) => {
                const existingItems = prevState[field] || [];
                const newItems = payload.filter(
                    item => !existingItems.some((existing: { _id: any }) => existing._id === item._id),
                );

                return {
                    ...prevState,
                    [field]: [...existingItems, ...newItems],
                };
            });
        }, []);

        const onEditCLick = useCallback(() => {
            setOpenDetailsForm(prev => !prev);
        }, []);

        return (
            <Container maxWidth={false}>
                <Stack py={2} direction={{ xs: 'column', sm: 'row' }} gap={1}>
                    <Stack sx={{ flex: 0.3 }}>
                        {isEditMode && (
                            <Box mb={2} px={2}>
                                <CustomButton
                                    label={savingMedia ? 'Saving...' : 'Save Media'}
                                    onClick={handleSaveMedia}
                                    disabled={savingMedia}
                                    customStyle={{
                                        borderRadius: '5px',
                                        width: '100%',
                                    }}
                                    sx={{
                                        '&:hover': {
                                            WebkitTextFillColor: theme[themeStyle].primary_orange,
                                        },
                                    }}
                                />
                            </Box>
                        )}
                        {!isEditMode && (
                            <CustomLazyLoadImage
                                src={mediaForm.profile_picture}
                                withPreview
                                isEditMode={isEditMode}
                                onEditClick={() => handleUpload('profile_picture')}
                                style={{
                                    height: 'auto',
                                    maxWidth: device.isMobile ? '100%' : '300px',
                                    objectFit: 'cover',
                                    borderRadius: 5,
                                }}
                            />
                        )}
                        {isEditMode && (
                            <PhotoUploadedWithCropAndCompress
                                showDisplay
                                customAspectRation={0.7}
                                style={{
                                    height: '450px',
                                    maxWidth: device.isMobile ? '100%' : '300px',
                                    objectFit: 'cover',
                                    borderRadius: 5,
                                }}
                                initialValue={mediaForm.profile_picture}
                                aspectRatio="square"
                                setPreview={function (url: string, file: File): void {
                                    handleMediaChange('profile_picture', url);
                                    setUploads((prevState: any) => ({
                                        ...prevState,
                                        profile_picture: [file],
                                    }));
                                }}
                            />
                        )}

                        <UserProjectSearchDialogue
                            open={openSearch}
                            type={mediaType}
                            enableAdd={mediaType === 'audio'}
                            onClose={() => setOpenSearch(!openSearch)}
                            onSubmit={payload => {
                                onSubmit(payload, mediaType === 'audio' ? 'audio_reel' : 'demo_reel');
                            }}
                        />
                        <input
                            type="file"
                            accept="image/*"
                            multiple={uploadField === 'photos'}
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <PhotoGrid
                            photos={mediaForm.photos}
                            isEditMode={isEditMode}
                            onAddPhoto={() => handleUpload('photos')}
                            onEditClick={index => {
                                setMediaForm(prevState => ({
                                    ...prevState,
                                    photos: mediaForm.photos.filter((_, i) => i !== index),
                                }));
                            }}
                        />
                        {workProfile && <LinkBox link={generateShareLink('profile', workProfile, true)} />}

                        <DemoReelGrid
                            projects={mediaForm.demo_reel}
                            themeStyle={themeStyle}
                            isEditMode={isEditMode}
                            onClickPlay={handleOnClickPlayForActsAndSorts}
                            onDelete={project => {
                                setMediaForm(prevState => ({
                                    ...prevState,
                                    demo_reel: mediaForm.demo_reel.filter(proj => proj._id !== project._id),
                                }));
                            }}
                            onAddNewItem={() => {
                                setMediaType('video');
                                setOpenSearch(!openSearch);
                            }}
                        />
                        {workProfile?.profile_type !== 'content_creator' && (
                            <AudioReelGrid
                                sounds={mediaForm.audio_reel}
                                themeStyle={themeStyle}
                                isEditMode={isEditMode}
                                onDelete={audio => {
                                    setMediaForm(prevState => ({
                                        ...prevState,
                                        demo_reel: mediaForm.demo_reel.filter(proj => proj._id !== audio._id),
                                    }));
                                }}
                                onAddNewItem={() => {
                                    setMediaType('audio');
                                    setOpenSearch(!openSearch);
                                }}
                            />
                        )}
                    </Stack>
                    <Stack sx={{ flex: 1 }}>
                        {isPreviewMode && onAcceptApplication && onRejectApplication && onMessageApplication && (
                            <Box mb={3}>{/* PreviewModeActions would be here if we implemented it */}</Box>
                        )}
                        {applicationData && (
                            <ApplicationMediaAndNotes
                                themeStyle={themeStyle}
                                attached_media={applicationData.attached_media}
                                note={applicationData.note}
                                onClickPlay={handleOnClickPlayForActsAndSorts}
                            />
                        )}
                        <CastingDetails
                            profile={form}
                            themeStyle={themeStyle}
                            isEditMode={isEditMode}
                            onEditClick={onEditCLick}
                            previewMode={!isSignedIn || (isSignedIn && getIdForIbjects(form.user) !== user?._id)}
                            onSubmitForm={payload =>
                                onChange &&
                                onChange({
                                    body: payload,
                                    uploads: undefined,
                                })
                            }
                        />
                    </Stack>
                </Stack>
            </Container>
        );
    },
);

export { CastingProfile };
